// @section 1 Strategy — Atlas internal strategy surface (read-only spike)
//
// Adam's brainstorm: Strategy = the agreed long-term frame anyone can read.
// BD operates opportunistically; alignment is CHECKABLE, not enforced. Pre-
// revenue today, so quantitative targets are explicitly deferred.
//
// Maps to OptiqOS's 6-layer cascade but slimmed for Vertis's pre-revenue
// state:
//   1.  Endgame              — Adam's why (4-archetype pick + qualitative)
//   1'. Identity             — mission/vision/positioning/ICP/anti-ICP
//   3.  Annual frame         — revenue streams + doctrine + R&D priorities
//                              (free-text bullets; numeric targets null)
//   4.  Active initiatives   — existing hq_strategy cascade
//   NEW. Alignment check     — deals vs. the frame (Atlas-specific)
//
// READ-ONLY SPIKE — Endgame/Identity/Annual placeholders are hardcoded to
// show the SHAPE. Active initiatives + Alignment check pull from real data.
// No schema change, no writes, no API additions.

import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { getCascade, listAccounts, listDeals, type AccountListItem, type Deal } from '../api';
import { useIsMobile } from '../lib/useIsMobile';

export default function Strategy() {
  // @block 1.0 Phone: own the scroll for the long cascade page.
  const isMobile = useIsMobile();
  return (
    <div style={{
      display: 'grid', gap: 18, maxWidth: 1200,
      ...(isMobile ? { height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px 14px' } : null),
    }}>
      {/* @block 1.1 Spike disclaimer banner */}
      <div style={{
        padding: '10px 14px', border: '1px dashed #2a3346', borderRadius: 6,
        background: 'transparent', fontSize: 12, color: '#a3b1c2', lineHeight: 1.6,
      }}>
        <strong style={{ color: '#e6e8ee' }}>Spike · read-only.</strong> Endgame / Identity / Annual frame
        sections show placeholder content to validate the structure. The Active Initiatives section reads
        the existing <code>hq_strategy</code> cascade live. The Alignment Check is brand-new — every
        deal across every account shows as "untagged · needs conversation" until a tagging mechanic exists.
      </div>

      <h1 style={{ margin: 0, fontSize: 28, lineHeight: 1.1 }}>Strategy</h1>
      <div style={{ fontSize: 13, color: '#6b7a90', marginTop: -8 }}>
        The agreed frame for Vertis Group. Long-term · collective · pre-revenue.
      </div>

      <EndgameSection />
      <IdentitySection />
      <AnnualFrameSection />
      <CriticalAndVipsRow />
      <ActiveInitiativesSection />
      <AlignmentCheckSection />
    </div>
  );
}

// @section 2 Endgame — Adam's why (Layer 1)
// Mock content per OptiqOS StrategyEndgame model; values are illustrative.
function EndgameSection() {
  const archetypes = [
    { key: 'cash_exit',     label: 'Cash Exit',      hint: 'sell within a defined window' },
    { key: 'legacy_family', label: 'Legacy / Family', hint: 'multi-generational hold' },
    { key: 'lifestyle',     label: 'Lifestyle',      hint: 'optimize for owner freedom' },
    { key: 'stewardship',   label: 'Stewardship',    hint: 'ESOP / employee handoff' },
  ];
  const selected = 'cash_exit'; // placeholder
  const isMobile = useIsMobile();

  return (
    <Section title="Endgame" subtitle="Layer 1 · Owner's Plan · the why behind the business">
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 8 }}>
        {archetypes.map((a) => {
          const isSel = a.key === selected;
          return (
            <div key={a.key} style={{
              padding: 12, borderRadius: 6,
              border: `1px solid ${isSel ? '#7eb6e8' : '#232a37'}`,
              background: isSel ? '#7eb6e811' : '#1d2330',
              opacity: isSel ? 1 : 0.55,
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e6e8ee' }}>
                {a.label}{isSel && <span style={{ marginLeft: 8, fontSize: 10, color: '#7eb6e8' }}>✓ selected</span>}
              </div>
              <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 4, lineHeight: 1.4 }}>{a.hint}</div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 12 }}>
        <KvRow label="Exit window">5-7 years from first material revenue (placeholder)</KvRow>
        <KvRow label="Target exit valuation">Defer — pre-revenue</KvRow>
        <KvRow label="Annual income target">Defer — pre-revenue</KvRow>
        <KvRow label="Strategic outcome">Vertis as the connective tissue layer above SMB systems</KvRow>
      </div>
    </Section>
  );
}

// @section 3 Identity — mission, vision, positioning, ICP, anti-ICP, values
function IdentitySection() {
  return (
    <Section title="Identity" subtitle="Layer 1' · who Vertis is, who it serves, who it doesn't">
      <div style={{ display: 'grid', gap: 12 }}>
        <KvRow label="Mission">
          Help small businesses run better by exposing the data + systems they already own.
        </KvRow>
        <KvRow label="Vision">
          A flywheel where OptiqOS powers Vertis's own ops, and Vertis's own ops shape OptiqOS.
        </KvRow>
        <KvRow label="Positioning">
          A consulting-grade operating system for SMB owners — not just dashboards, the actual workflow.
        </KvRow>
        <KvRow label="Category">B2B SaaS · SMB operations · vertical-specific</KvRow>
        <KvRow label="Ideal Customer Profile (ICP)">
          $1M-$20M revenue, 10-100 employees, owner still close to the work, multiplier potential
          (vertical foothold or referral network).
        </KvRow>
        <KvRow label="Anti-ICP">
          Sub-$500k revenue · single-shot project asks · clients who buy data not outcomes.
        </KvRow>
        <KvRow label="Values">
          Integrity · Build the thing · Trust the operator · Compound over time
        </KvRow>
      </div>
    </Section>
  );
}

// @section 4 Annual frame — revenue streams / doctrine / R&D priorities
// Maps to OptiqOS AnnualPlan but qualitative-only for pre-revenue Vertis.
function AnnualFrameSection() {
  const isMobile = useIsMobile();
  return (
    <Section title="Annual frame" subtitle="Layer 3 · what we're pursuing in 2026 · quantitative targets deferred until revenue">
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12 }}>
        <FrameCard title="Revenue streams" tone="#6ec78a">
          <FrameItemRow icon="✓" tone="#6ec78a" label="CPA-bookkeeper retainers" />
          <FrameItemRow icon="✓" tone="#6ec78a" label="OptiqOS deployments (SMB)" />
          <FrameItemRow icon="?" tone="#f0c674" label="Drone services (exploring)" />
          <FrameItemRow icon="?" tone="#f0c674" label="Three Sides creative IP (exploring)" />
          <FrameItemRow icon="✗" tone="#6b7a90" label="Market-data one-time sales" />
          <FrameItemRow icon="✗" tone="#6b7a90" label="Single-shot custom builds" />
        </FrameCard>

        <FrameCard title="Engagement doctrine" tone="#7eb6e8">
          <FrameBulletRow>Multiplier potential — referral network or vertical foothold</FrameBulletRow>
          <FrameBulletRow>Recurring &gt; project-once</FrameBulletRow>
          <FrameBulletRow>Owner is close to the work + still calling shots</FrameBulletRow>
          <FrameBulletRow>Capabilities we can productize, not bespoke</FrameBulletRow>
          <FrameBulletRow>Min size deferred — opportunistic during pre-revenue</FrameBulletRow>
        </FrameCard>

        <FrameCard title="R&D priorities" tone="#a08fd8">
          <FrameBulletRow>Connectors that work across verticals (Bridge API growth)</FrameBulletRow>
          <FrameBulletRow>Productizing existing capabilities (audit · enrichment · campaigns)</FrameBulletRow>
          <FrameBulletRow>Atlas-as-operating-system for Vertis itself (this product)</FrameBulletRow>
          <FrameBulletRow>AI orchestration layer (Helix · Claude agents)</FrameBulletRow>
        </FrameCard>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: '#6b7a90', fontStyle: 'italic' }}>
        Quantitative targets (revenue, leads, customers, profit) deferred until pre-revenue ends.
        The numeric columns on <code>hq_strategy.annual_plan</code> stay null for now.
      </div>
    </Section>
  );
}

// @section 4.5 Critical Initiatives + VIPs — paired "essential focus" row
//
// Both are curated headline lists, distinct from the broader cascade / CRM:
//   - Critical Initiatives = the 3-5 moves that MUST happen this quarter
//   - VIPs                 = the people the relationship must stay warm with,
//                            independent of any specific deal
//
// Read-only spike. Hardcoded placeholders show the shape; the next pass would
// add a `is_critical` flag on hq_strategy.initiatives and a `hq_strategy.vips`
// pointer table that references hq_crm.contacts across accounts (the one place
// in Atlas where cross-account person linkage IS deliberate).
function CriticalAndVipsRow() {
  const isMobile = useIsMobile();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.2fr) minmax(0, 1fr)', gap: 18 }}>
      <CriticalInitiativesSection />
      <VipsSection />
    </div>
  );
}

function CriticalInitiativesSection() {
  // Placeholder — grounded in Adam's actual current threads so the layout
  // reads as if Vertis-state, not generic. Next pass: read from
  // hq_strategy.initiatives WHERE is_critical = true.
  const items = [
    {
      title: 'Land first paying CPA/bookkeeper retainer',
      detail: 'Convert one of the 7 warm replies from the CPA/BK campaign into a $4k+ MRR deal. Unlocks the recurring stream in the Annual frame.',
      owner: 'Adam',
      status: 'in-progress',
    },
    {
      title: 'Ship OptiqOS demo at Medacare',
      detail: 'EZ thread; demo + deck delivered, awaiting follow-up. Unlocks EZ network (Fantaz, Canelo, auto-dealer groups).',
      owner: 'Adam · Patrick',
      status: 'in-progress',
    },
    {
      title: 'Complete Sawa\'s OptiqOS deployment',
      detail: 'QBO live; restaurant OS feature strategy (recipe viewer → receiving → demand forecast). First reference customer.',
      owner: 'Adam',
      status: 'in-progress',
    },
    {
      title: 'BCTS data pipeline · next file due 5/30',
      detail: 'Building-intel ZIP report prototype for Julian Palacios. Validates the data-product line and Vertis Holdings positioning.',
      owner: 'Adam',
      status: 'due-soon',
    },
    {
      title: 'Atlas merger to prod swap',
      detail: 'Once Workplan iteration completes (current session). Turns Atlas into the operating system for Vertis itself — the flywheel claim.',
      owner: 'Adam',
      status: 'staging',
    },
  ];

  return (
    <Section title="Critical Initiatives" subtitle="the moves that MUST happen this quarter">
      <div style={{ display: 'grid', gap: 10 }}>
        {items.map((it, i) => (
          <div key={i} style={{
            padding: 12, border: '1px solid #232a37', borderRadius: 6,
            background: '#1d2330', display: 'flex', gap: 12,
          }}>
            <span style={{
              fontSize: 18, fontWeight: 700, color: '#a3b1c2', minWidth: 24, textAlign: 'center',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {i + 1}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#e6e8ee' }}>{it.title}</div>
                <StatusPip status={it.status} />
              </div>
              <div style={{ fontSize: 12, color: '#a3b1c2', lineHeight: 1.45, marginTop: 4 }}>
                {it.detail}
              </div>
              <div style={{ fontSize: 10, color: '#6b7a90', marginTop: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
                {it.owner}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 10, fontStyle: 'italic' }}>
        Placeholder · next pass: <code>is_critical</code> flag on <code>hq_strategy.initiatives</code> +
        an "elevate to critical" pin from the cascade.
      </div>
    </Section>
  );
}

function VipsSection() {
  // Placeholder — drawn from Adam's actual network. Each VIP is a person
  // whose relationship matters at the strategy layer regardless of which
  // deal they sit on today. Next pass: hq_strategy.vips table that points
  // at one-or-many hq_crm.contacts rows (the deliberate cross-account
  // linkage exception to the Andrew-Sawa no-auto-merge rule).
  const items = [
    {
      name: 'Julian Palacios',
      role: 'CEO · Vertis Holdings · BCTS',
      why: 'Capital allocation. WFG $30M POF. Building-intel pipeline.',
      lastTouch: 'last week',
    },
    {
      name: 'Patrick Jeffries',
      role: 'BD partner · VPG · NaviPoint',
      why: 'Deal-flow engine. Patrick is the BD half of Atlas.',
      lastTouch: 'today',
    },
    {
      name: 'EZ (Esteban Zwirn)',
      role: 'US/Mexico lobbyist · La Quinta',
      why: 'Network multiplier — Medacare, Fantaz, Canelo, auto-dealer groups.',
      lastTouch: '2 days ago',
    },
    {
      name: 'Isaac Batu',
      role: 'WorldWeb Accounting · referrer',
      why: 'Warm CPA-vertical referrer. Demo\'d 5/4. Test of the referral kit.',
      lastTouch: '3 weeks ago',
    },
    {
      name: 'Andrew Sawa',
      role: 'CBRE · institutional contact',
      why: 'NDA signed. Pure waiting on data-room + systems access.',
      lastTouch: '1 week ago',
    },
    {
      name: 'Darren Davis',
      role: 'Conversiv · potential partner',
      why: 'GTM partnership waiting on their plan. Met 5/22.',
      lastTouch: '5 days ago',
    },
    {
      name: 'Julie Caccamise',
      role: 'Quality Matters · family + early customer',
      why: 'Two projects (website rebuild, invoice tool). Loyalty + reference.',
      lastTouch: 'late-June ping',
    },
  ];

  return (
    <Section title="VIPs" subtitle="people the relationship must stay warm with">
      <div style={{ display: 'grid', gap: 8 }}>
        {items.map((v, i) => (
          <div key={i} style={{
            padding: '10px 12px', border: '1px solid #232a37', borderRadius: 6,
            background: '#1d2330',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e6e8ee' }}>{v.name}</div>
              <div style={{ fontSize: 10, color: '#6b7a90' }}>last touch · {v.lastTouch}</div>
            </div>
            <div style={{ fontSize: 11, color: '#7eb6e8', marginTop: 2 }}>{v.role}</div>
            <div style={{ fontSize: 12, color: '#a3b1c2', marginTop: 4, lineHeight: 1.4 }}>{v.why}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 10, fontStyle: 'italic' }}>
        Placeholder · next pass: <code>hq_strategy.vips</code> pointing at
        one-or-many <code>hq_crm.contacts</code> rows (the deliberate cross-account exception).
      </div>
    </Section>
  );
}

function StatusPip({ status }: { status: string }) {
  const tone = status === 'in-progress' ? '#7eb6e8'
    : status === 'due-soon' ? '#f0a96b'
    : status === 'staging' ? '#a08fd8'
    : status === 'done' ? '#6ec78a'
    : '#6b7a90';
  return (
    <span style={{
      fontSize: 9, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: 1,
      background: `${tone}22`, color: tone, flexShrink: 0,
    }}>
      {status}
    </span>
  );
}

// @section 5 Active initiatives — REAL data from hq_strategy.cascade
function ActiveInitiativesSection() {
  const [cascade, setCascade] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { getCascade().then(setCascade).catch((e) => setErr(String(e))); }, []);

  return (
    <Section title="Active initiatives" subtitle="Layer 4 · what we're working on right now (live from hq_strategy)">
      {err && <div style={{ color: '#e87e7e', fontSize: 12 }}>{err}</div>}
      {!cascade && !err && <div style={{ color: '#6b7a90', fontSize: 12 }}>Loading…</div>}
      {cascade && !cascade.vision && (
        <em style={{ color: '#6b7a90', fontSize: 13 }}>
          No active vision yet. The existing /strategy cascade is empty —
          POST to <code>/api/hq/strategy/visions</code> to seed it.
        </em>
      )}
      {cascade && cascade.vision && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#e6e8ee', marginBottom: 8 }}>
            {cascade.vision.title}
          </div>
          {cascade.objectives.length === 0 && (
            <em style={{ color: '#6b7a90', fontSize: 13 }}>No objectives under this vision yet.</em>
          )}
          <div style={{ display: 'grid', gap: 10 }}>
            {cascade.objectives.map((o: any) => (
              <div key={o.id} style={{
                padding: 10, border: '1px solid #232a37', borderRadius: 6, background: '#1d2330',
              }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#e6e8ee' }}>
                  {o.title}
                  <span style={{ fontSize: 10, color: '#6b7a90', marginLeft: 8 }}>· {o.status}</span>
                </div>
                {o.initiatives.length === 0 ? (
                  <em style={{ fontSize: 11, color: '#6b7a90' }}>No initiatives.</em>
                ) : (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, color: '#a3b1c2', lineHeight: 1.6 }}>
                    {o.initiatives.map((i: any) => (
                      <li key={i.id}>
                        <strong style={{ color: '#e6e8ee' }}>{i.title}</strong>
                        {i.quarter && <span style={{ color: '#6b7a90', marginLeft: 6 }}>{i.quarter}</span>}
                        <span style={{ color: '#6b7a90', marginLeft: 6 }}>· {i.status}</span>
                        {i.bets && i.bets.length > 0 && (
                          <ul style={{ margin: '2px 0 0', paddingLeft: 16, fontSize: 11 }}>
                            {i.bets.map((b: any) => (
                              <li key={b.id}>{b.title} <span style={{ color: '#6b7a90' }}>· {b.status}</span></li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

// @section 6 Alignment check — the Atlas-specific NEW piece
// Every deal across every account, grouped by alignment chip.
// Today: all "untagged" since no tagging mechanic exists. The section's
// purpose is to make the gap visible so the conversation happens.
function AlignmentCheckSection() {
  const [accounts, setAccounts] = useState<AccountListItem[] | null>(null);
  const [deals, setDeals] = useState<Deal[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([listAccounts({ take: 500 }), listDeals()])
      .then(([a, d]) => { setAccounts(a); setDeals(d); })
      .catch((e) => setErr(String(e)));
  }, []);

  if (err) return (
    <Section title="Alignment check" subtitle="NEW · BD pipeline vs. the agreed frame">
      <div style={{ color: '#e87e7e', fontSize: 12 }}>{err}</div>
    </Section>
  );

  if (!accounts || !deals) return (
    <Section title="Alignment check" subtitle="NEW · BD pipeline vs. the agreed frame">
      <div style={{ color: '#6b7a90', fontSize: 12 }}>Loading…</div>
    </Section>
  );

  const accountName = (id?: string | null) =>
    id ? (accounts.find((a) => a.id === id)?.name ?? id.slice(0, 8)) : '(no account)';

  // For the spike: all deals are "untagged" since no tagging field exists yet.
  // The visual SHOWS where on-strategy / opportunistic buckets would live so
  // Adam can see the model. Stub the on-strategy + opportunistic buckets with
  // 0-count states so the layout is the actual final layout.
  const openDeals = deals.filter((d) => !d.closedAt);
  const onStrategy: Deal[] = [];
  const opportunistic: Deal[] = [];
  const untagged: Deal[] = openDeals;

  return (
    <Section
      title="Alignment check"
      subtitle="NEW · open deals vs. the agreed frame · the Atlas-specific layer"
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <AlignmentColumn
          title="On strategy"
          hint="fits revenue streams + doctrine"
          tone="#6ec78a"
          count={onStrategy.length}
          emptyHint="Once the tagging chip lands, on-strategy deals show here."
          deals={onStrategy}
          accountName={accountName}
        />
        <AlignmentColumn
          title="Opportunistic"
          hint="Patrick's call — outside doctrine, explicit OK"
          tone="#f0c674"
          count={opportunistic.length}
          emptyHint="Off-doctrine deals Patrick explicitly approved show here."
          deals={opportunistic}
          accountName={accountName}
        />
        <AlignmentColumn
          title="Untagged · needs conversation"
          hint="not yet decided"
          tone="#7eb6e8"
          count={untagged.length}
          emptyHint="(no open deals)"
          deals={untagged}
          accountName={accountName}
        />
      </div>

      <div style={{
        marginTop: 12, padding: 10, border: '1px dashed #2a3346', borderRadius: 6,
        fontSize: 11, color: '#6b7a90', lineHeight: 1.6,
      }}>
        <strong style={{ color: '#a3b1c2' }}>Why this section exists:</strong> OptiqOS Strategy
        doesn't have an Alignment Check because client orgs don't usually have a separate BD operator
        running opportunistic deals. Vertis does — Patrick. The chip on each deal
        ({' '}<code>strategy_alignment</code>{' '}on <code>hq_crm.deals</code>) is the smallest
        write that closes this loop. Today nothing here is tagged, which is itself the signal.
      </div>
    </Section>
  );
}

function AlignmentColumn({ title, hint, tone, count, emptyHint, deals, accountName }: {
  title: string; hint: string; tone: string; count: number; emptyHint: string;
  deals: Deal[]; accountName: (id?: string | null) => string;
}) {
  return (
    <div style={{
      padding: 12, border: '1px solid #232a37', borderRadius: 8,
      background: '#171c26', display: 'flex', flexDirection: 'column', gap: 8,
      minHeight: 120,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: tone, textTransform: 'uppercase', letterSpacing: 1.5 }}>
            {title}
          </div>
          <div style={{ fontSize: 10, color: '#6b7a90', marginTop: 2 }}>{hint}</div>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
          background: '#1d2330', color: tone,
        }}>
          {count}
        </span>
      </div>
      {deals.length === 0 ? (
        <em style={{ fontSize: 11, color: '#6b7a90', padding: '6px 0' }}>{emptyHint}</em>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
          {deals.slice(0, 12).map((d) => (
            <li key={d.id} style={{
              padding: '6px 8px', border: '1px solid #232a37', borderRadius: 4,
              background: '#1d2330', fontSize: 12,
            }}>
              <div style={{ color: '#e6e8ee' }}>{d.name}</div>
              <div style={{ fontSize: 10, color: '#6b7a90', marginTop: 2 }}>
                {accountName(d.companyId)} · {d.stage}
                {d.valueCents != null && <> · ${(d.valueCents / 100).toLocaleString()}</>}
              </div>
            </li>
          ))}
          {deals.length > 12 && (
            <li style={{ fontSize: 11, color: '#6b7a90', textAlign: 'center', padding: '4px 0' }}>
              + {deals.length - 12} more
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// @section 7 Shared bits
function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section style={sectionStyle}>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, color: '#e6e8ee', fontWeight: 600 }}>{title}</h2>
        {subtitle && <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 2, letterSpacing: 0.3 }}>{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function KvRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 14, fontSize: 13, paddingBottom: 6, borderBottom: '1px solid #232a3744' }}>
      <span style={{
        fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5,
        color: '#6b7a90', minWidth: 140, paddingTop: 2, flexShrink: 0,
      }}>
        {label}
      </span>
      <div style={{ flex: 1, color: '#e6e8ee', lineHeight: 1.5 }}>{children}</div>
    </div>
  );
}

function FrameCard({ title, tone, children }: { title: string; tone: string; children: React.ReactNode }) {
  return (
    <div style={{
      padding: 12, border: `1px solid ${tone}44`, borderRadius: 6,
      background: '#1d2330',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: tone,
        textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 4,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function FrameItemRow({ icon, tone, label }: { icon: string; tone: string; label: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 13, color: '#e6e8ee' }}>
      <span style={{ fontWeight: 700, color: tone, minWidth: 12, textAlign: 'center' }}>{icon}</span>
      <span>{label}</span>
    </div>
  );
}

function FrameBulletRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 13, color: '#e6e8ee', lineHeight: 1.45 }}>
      <span style={{ color: '#6b7a90', minWidth: 12, textAlign: 'center' }}>•</span>
      <span>{children}</span>
    </div>
  );
}

const sectionStyle: CSSProperties = {
  padding: '16px 18px',
  border: '1px solid #232a37',
  borderRadius: 8,
  background: '#171c26',
};
